import {
    BarChartOutlined,
    EnvironmentOutlined,
    InboxOutlined,
    LineChartOutlined,
    SettingOutlined,
    TeamOutlined,
    UserOutlined,
    CloudUploadOutlined,
    ToolOutlined,
    SafetyCertificateOutlined,
    ControlOutlined,
} from '@ant-design/icons';
import React, { useEffect, useMemo } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import MainLayout from './MainLayout';
import { useTranslation } from 'react-i18next';
import { hasHeadquartersAccess, isHeadquartersAdmin, isFinancialTeam,isDspFinancialTeam,isIncomeFinancialTeam, isKAQC } from 'utils/auth';
import { findFirstAvailableMenuItem } from 'utils/menuUtils';
import { hasResource } from 'utils/resourceAuth';
import { ResourceCode } from 'constants/resourceCodes';
import { renderHistoryMenuLabel } from 'components/parcelQueryRange/HistoryMenuTag';

const CentralLayout: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();

    const menuItems = useMemo(
        () => [
        {
            key: 'progress',
            label: t('central.progress.title'),
            display: false,
            icon: <LineChartOutlined/>,
            onClick: () => navigate('/central/progress')
        },
        {
            key: 'reports',
            label: t('central.reports.title'),
            display: false,
            icon: <BarChartOutlined/>,
            onClick: () => navigate('/central/reports')
        },
        {
            key: 'parcels-submenu',
            label: t('central.parcels.title'),
            icon: <InboxOutlined/>,
            display: hasHeadquartersAccess() || isFinancialTeam() || isDspFinancialTeam() || isIncomeFinancialTeam(),
            children: [
                {
                    key: 'parcels',
                    label: t('central.parcels.menu.list'),
                    display: true,
                    onClick: () => navigate('/central/parcels')
                },
                {
                    key: 'parcels/history',
                    label: renderHistoryMenuLabel(t('central.parcels.menu.history')),
                    display: true,
                    onClick: () => navigate('/central/parcels/history')
                },
                {
                    key: 'parcels/damaged-label-lookup',
                    label: t('central.parcels.menu.damagedLabelLookup'),
                    display: true,
                    onClick: () => navigate('/central/parcels/damaged-label-lookup')
                }
            ]
        },
        {
            key: 'settings/sites',
            icon: <EnvironmentOutlined />,
            label: t('central.sites.title'),
            display: hasHeadquartersAccess() || isFinancialTeam() || isDspFinancialTeam() || isIncomeFinancialTeam(),
            children: [
                {
                    key: 'settings/sites/management',
                    label: t('central.sites.management'),
                    display: hasHeadquartersAccess() || isFinancialTeam() || isDspFinancialTeam() || isIncomeFinancialTeam(),
                    onClick: () => navigate('/central/settings/sites/management')
                },
                {
                    key: 'settings/sites/map',
                    label: t('central.sites.map'),
                    display: hasHeadquartersAccess() || isFinancialTeam() || isDspFinancialTeam() || isIncomeFinancialTeam(),
                    onClick: () => navigate('/central/settings/sites/map')
                },
                {
                    key: 'settings/sites/transit',
                    label: 'Transit Management',
                    display: false,
                    onClick: () => navigate('/central/settings/sites/transit')
                },
                {
                    key: 'settings/sites/zipcodes',
                    label: t('central.zipcodes.title'),
                    display: hasHeadquartersAccess() || isFinancialTeam() || isIncomeFinancialTeam(),
                    onClick: () => navigate('/central/settings/sites/zipcodes')
                }
            ]
        },
        {
            key: 'channels',
            label: t('central.channels.title'),
            icon: <UserOutlined/>,
            display: hasResource([ResourceCode.crm.CRM_CHANNEL]),
            onClick: () => navigate('/central/channels')
        },
        {
            key: 'capacity',
            label: t('central.capacity.title'),
            icon: <TeamOutlined />,
            display: hasResource([ResourceCode.menu.TRANSPORT_CAPACITY]),
            children: [
                {
                    key: 'account',
                    label: t('central.personnel.title'),
                    display: isHeadquartersAdmin(),
                    onClick: () => navigate('/central/account')
                },
                {
                    key: 'dsp',
                    label: t('central.dsp.title'),
                    display: hasResource([ResourceCode.menu.DELIVERY_SERVICE_PARTNER]),
                    onClick: () => navigate('/central/dsp')
                },
                {
                    key: 'delivery-driver',
                    label: t('wms.deliveryDriver.title'),
                    display: hasResource([ResourceCode.menu.TRANSPORT_DELIVERY_DRIVER]),
                    onClick: () => navigate('/central/delivery-driver')
                },
                {
                    key: 'carrier',
                    label: t('central.carrier.title'),
                    display: hasHeadquartersAccess() || isFinancialTeam(),
                    onClick: () => navigate('/central/carrier')
                },
                {
                    key: 'handover-carrier',
                    label: t('central.handoverCarrier.title'),
                    display: hasHeadquartersAccess() || isFinancialTeam(),
                    onClick: () => navigate('/central/handover-carrier')
                },
                {
                    key: 'vehicle-model',
                    label: t('central.vehicleModel.title'),
                    display: hasHeadquartersAccess() || isFinancialTeam(),
                    onClick: () => navigate('/central/vehicle-model')
                }
            ]
        },
        {
            key: 'download',
            label: t('central.download.title'),
            display: hasHeadquartersAccess() || isFinancialTeam() || isDspFinancialTeam() || isIncomeFinancialTeam(),
            icon: <BarChartOutlined/>,
            onClick: () => navigate('/central/download')
        },
        {
            key: 'order-quota',
            label: t('central.orderQuota.title'),
            icon: <ControlOutlined />,
            display: hasResource([ResourceCode.menu.ONBOARDING_ORDER_QUOTA]),
            children: [
                {
                    key: 'order-quota/customer-pickup-hub',
                    label: t('central.orderQuota.menu.customerPickupHub'),
                    display: hasResource([ResourceCode.menu.ONBOARDING_ORDER_QUOTA]),
                    onClick: () => navigate('/central/order-quota/customer-pickup-hub')
                },
                {
                    key: 'order-quota/dest-warehouse',
                    label: t('central.orderQuota.menu.destWarehouse'),
                    display: hasResource([ResourceCode.menu.ONBOARDING_ORDER_QUOTA]),
                    onClick: () => navigate('/central/order-quota/dest-warehouse')
                }
            ]
        },
        {
            key: 'settings/client-hub-zone',
            icon: <SettingOutlined />,
            // Hidden pending feature flag configuration
            label: 'Client Hub Zone',
            display: false,
            onClick: () => navigate('/central/settings/client-hub-zone')
        },
        {
            key: 'customs-clearance',
            label: t('central.customsClearance.title'),
            icon: <InboxOutlined />,
            display: false,
            onClick: () => navigate('/central/customs-clearance')
        },
        {
            key: 'platform-permission',
            label: t('central.platformPermission.title'),
            icon: <SafetyCertificateOutlined />,
            display: hasResource([ResourceCode.menu.PLATFORM_PERMISSION]),
            children: [
                {
                    key: 'platform-permission/organization',
                    label: t('central.platformPermission.organizationStructureMenu'),
                    display: hasResource([ResourceCode.menu.DEPT_TREE_QUERY]),
                    onClick: () => navigate('/central/platform-permission/organization')
                },
                {
                    key: 'platform-permission/personnel',
                    label: t('central.platformPermission.personnelMenu'),
                    display: hasResource([ResourceCode.menu.PERSONNEL_QUERY]),
                    onClick: () => navigate('/central/platform-permission/personnel')
                },
                {
                    key: 'platform-permission/role',
                    label: t('central.platformPermission.roleMenu'),
                    display: hasResource([ResourceCode.menu.ROLE_QUERY]),
                    onClick: () => navigate('/central/platform-permission/role')
                },
                {
                    key: 'platform-permission/permission-package',
                    label: t('central.platformPermission.permissionPackageMenu'),
                    display: hasResource([ResourceCode.menu.PERMISSION_PACKAGE_QUERY]),
                    onClick: () => navigate('/central/platform-permission/permission-package')
                },
                {
                    key: 'platform-permission/resource',
                    label: t('central.platformPermission.functionPermissionMenu'),
                    display: hasResource([ResourceCode.menu.RESOURCE_QUERY]),
                    onClick: () => navigate('/central/platform-permission/resource')
                },
                {
                    key: 'platform-permission/operation-log',
                    label: t('central.platformPermission.permissionOperationLogMenu'),
                    display: hasResource([ResourceCode.menu.PERMISSION_OPERATION_LOG_QUERY]),
                    onClick: () => navigate('/central/platform-permission/operation-log')
                }
            ]
        },
        {
            key: 'pod-upload',
            label: 'POD Re-upload',
            icon: <CloudUploadOutlined />,
            display: false,
            onClick: () => navigate('/central/pod-upload')
        },
        {
            key: 'map-tool',
            label: t('mapTool.title'),
            icon: <EnvironmentOutlined />,
            display: hasResource([ResourceCode.menu.MAP_TOOL]),
            children: [
                {
                    key: 'map-tool/location-correction',
                    label: t('mapTool.addressLibrary.locationCorrection'),
                    display: hasResource([ResourceCode.menu.LOCATION_CORRECTION]),
                    onClick: () => navigate('/central/map-tool/location-correction')
                },
                {
                    key: 'map-tool/geofence-blacklist-review',
                    label: t('mapTool.addressLibrary.geofenceBlacklistReview'),
                    display: hasResource([ResourceCode.menu.GEOFENCE_BLACKLIST_REVIEW]),
                    onClick: () => navigate('/central/map-tool/geofence-blacklist-review')
                },
                {
                    key: 'map-tool/ab-address-review',
                    label: t('mapTool.addressLibrary.abAddressReview'),
                    display: hasResource([ResourceCode.menu.AB_ADDRESS_REVIEW]),
                    onClick: () => navigate('/central/map-tool/ab-address-review')
                },
                {
                    key: 'map-tool/address-residential-area',
                    label: t('mapTool.addressLibrary.addressResidentialAreaProduction'),
                    display: hasResource([ResourceCode.menu.ADDRESS_RESIDENTIAL_AREA]),
                    onClick: () => navigate('/central/map-tool/address-residential-area')
                },
                {
                    key: 'map-tool/address-residential-area-pending',
                    label: t('mapTool.addressLibrary.addressResidentialAreaPending'),
                    display: hasResource([ResourceCode.menu.ADDRESS_RESIDENTIAL_AREA_PENDING]),
                    onClick: () => navigate('/central/map-tool/address-residential-area-pending')
                },
                {
                    key: 'map-tool/driver-location-track',
                    label: t('mapTool.driverLocationTrack.menu.trackQuery'),
                    display: hasResource([ResourceCode.menu.TRAJECTORY_PLATFORM]),
                    onClick: () => navigate('/central/map-tool/driver-location-track')
                },
                {
                    key: 'map-tool/aoi-platform',
                    label: t('mapTool.aoiPlatform.title'),
                    display: hasResource([ResourceCode.menu.AOI_PLATFORM]),
                    onClick: () => navigate('/central/map-tool/aoi-platform')
                },
                {
                    key: 'map-tool/standardized-address',
                    label: t('mapTool.addressLibrary.standardizedAddress'),
                    display: hasResource([ResourceCode.menu.STANDARDIZED_ADDRESS]),
                    onClick: () => navigate('/central/map-tool/standardized-address')
                },
                {
                    key: 'map-tool/address-lifecycle',
                    label: t('mapTool.addressLibrary.addressLifecycle'),
                    display: hasResource([ResourceCode.menu.ADDRESS_LIFECYCLE]),
                    onClick: () => navigate('/central/map-tool/address-lifecycle')
                },
                {
                    key: 'map-tool/address-whitelist',
                    label: t('mapTool.addressLibrary.addressWhitelist'),
                    display: hasResource([ResourceCode.menu.ADDRESS_WHITELIST]),
                    onClick: () => navigate('/central/map-tool/address-whitelist')
                },
                {
                    key: 'map-tool/address-whitelist-rule',
                    label: t('mapTool.addressLibrary.addressWhitelistRule'),
                    display: hasResource([ResourceCode.menu.ADDRESS_WHITELIST]),
                    onClick: () => navigate('/central/map-tool/address-whitelist-rule')
                },

                {
                    key: 'map-tool/address-blacklist',
                    label: t('mapTool.addressLibrary.addressBlacklist'),
                    display: hasResource([ResourceCode.menu.ADDRESS_BLACKLIST]),
                    onClick: () => navigate('/central/map-tool/address-blacklist')
                },
                {
                    key: 'map-tool/address-invalid',
                    label: t('mapTool.addressLibrary.addressInvalid'),
                    display: hasResource([ResourceCode.menu.ADDRESS_INVALID]),
                    onClick: () => navigate('/central/map-tool/address-invalid')
                },
                {
                    key: 'map-tool/address-coord-dedup',
                    label: t('mapTool.addressLibrary.addressCoordDedup'),
                    display: hasResource([ResourceCode.addressCoordDedup.VIEW]),
                    onClick: () => navigate('/central/map-tool/address-coord-dedup')
                },
            ]
        },
        {
            key: 'dev-tools',
            label: t('central.devTools.title'),
            icon: <ToolOutlined />,
            display: hasResource([ResourceCode.menu.DEV_TOOL]),
            children: [
                {
                    key: 'common-config',
                    label: t('commonConfig.title'),
                    display: hasResource([ResourceCode.menu.GENERAL_CONFIG]),
                    onClick: () => navigate('/central/common-config')
                },
                {
                    key: 'pod-capture-config',
                    label: 'POD拍摄配置',
                    display: hasResource([ResourceCode.menu.POD_CONFIG]),
                    onClick: () => navigate('/central/pod-capture-config')
                },
                {
                    key: 'pod-capture-config-v2',
                    label: 'POD拍摄配置V2',
                    display: hasResource([ResourceCode.menu.POD_CONFIG]),
                    onClick: () => navigate('/central/pod-capture-config-v2')
                },
                {
                    key: 'entry-config',
                    label: t('config.menu.entryConfig'),
                    display: hasResource([ResourceCode.menu.NODE_CONFIG]),
                    onClick: () => navigate('/central/entry-config')
                },
                {
                    key: 'degrade',
                    label: t('degrade.menu.title'),
                    display: hasResource([ResourceCode.menu.DEGRADE_MANAGEMENT]),
                    onClick: () => navigate('/central/degrade')
                },
                {
                    key: 'app-version',
                    label: t('central.appVersion.title'),
                    display: hasResource([ResourceCode.menu.APP_VERSION]),
                    onClick: () => navigate('/central/app-version')
                },
                {
                    key: 'pda-scan-config',
                    label: t('pdaScanConfig.title'),
                    display: hasResource([ResourceCode.menu.PDA_SCAN_CONFIG]),
                    onClick: () => navigate('/central/pda-scan-config')
                },
                {
                    key: 'performance-monitor',
                    label: t('mapTool.performanceMonitor.title'),
                    display: hasResource([ResourceCode.menu.PERFORMANCE_MONITOR]),
                    onClick: () => navigate('/central/performance-monitor')
                },
                {
                    key: 'pda-home-config',
                    label: t('pda.homeConfig.title'),
                    display: hasResource([ResourceCode.menu.PDA_HOME_CONFIG]),
                    onClick: () => navigate('/central/pda-home-config')
                },
                {
                    key: 'app-activity',
                    label: '活动配置',
                    display: hasResource([ResourceCode.menu.GENERAL_CONFIG]),
                    onClick: () => navigate('/central/app-activity')
                },
                {
                    key: 'learning-center-config',
                    label: t('learningCenterConfig.title'),
                    display: hasResource([ResourceCode.menu.DRIVER_LEARNING_CENTER_CONFIG]),
                    onClick: () => navigate('/central/learning-center-config')
                },
                {
                    key: 'operation-logs',
                    label: t('central.operationLogs.title'),
                    display: hasResource([ResourceCode.menu.SYSTEM_OPERATION_LOG_QUERY]),
                    onClick: () => navigate('/central/operation-logs')
                },
                {
                    key: 'support-tools/support-login',
                    label: t('supportTools.supportLogin.title'),
                    display: hasResource([ResourceCode.menu.SUPPORT_LOGIN]),
                    onClick: () => navigate('/central/support-tools/support-login')
                },
                {
                    key: 'support-tools/login-log',
                    label: t('supportTools.loginLog.title'),
                    display: hasResource([ResourceCode.menu.SUPPORT_LOGIN_LOG]),
                    onClick: () => navigate('/central/support-tools/login-log')
                },
                {
                    key: 'support-tools/redis-tools',
                    label: t('supportTools.redisTools.menuTitle'),
                    display: hasResource([ResourceCode.menu.SUPPORT_LOGIN_REDIS]),
                    onClick: () => navigate('/central/support-tools/redis-tools')
                },
                {
                    key: 'support-tools/dubbo-tools',
                    label: 'Dubbo 远程调试',
                    display: hasResource([ResourceCode.menu.DEV_TOOL]),
                    onClick: () => navigate('/central/support-tools/dubbo-tools')
                },
                {
                    key: 'support-tools/reflect-invoke',
                    label: '定时任务调用工具',
                    display: hasResource([ResourceCode.menu.DEV_TOOL]),
                    onClick: () => navigate('/central/support-tools/reflect-invoke')
                },
                {
                    key: 'support-tools/sms-tool',
                    label: '短信工具',
                    display: hasResource([ResourceCode.menu.DEV_TOOL]),
                    onClick: () => navigate('/central/support-tools/sms-tool')
                },
                {
                    key: 'support-tools/order-pool',
                    label: t('supportTools.orderPool.menuTitle'),
                    display: hasResource([ResourceCode.menu.SUPPORT_SCHEDULE_ORDER_POOL]),
                    onClick: () => navigate('/central/support-tools/order-pool')
                },
                {
                    key: 'support-tools/split-route-cache',
                    label: t('supportTools.splitRouteCache.menuTitle'),
                    display: hasResource([ResourceCode.menu.SUPPORT_SCHEDULE_ROUTE_CACHE]),
                    onClick: () => navigate('/central/support-tools/split-route-cache')
                },
                {
                    key: 'support-tools/es-tools',
                    label: t('supportTools.esTools.menuTitle'),
                    display: hasResource([ResourceCode.menu.SUPPORT_ORDER_ES]),
                    onClick: () => navigate('/central/support-tools/es-tools')
                },
                {
                    key: 'support-tools/upload',
                    label: t('supportTools.upload.menuTitle'),
                    display: hasResource([ResourceCode.menu.SUPPORT_UPLOAD_TOOL]),
                    onClick: () => navigate('/central/support-tools/upload')
                }
            ]
        },
    ],
        [navigate, t]
    );

    useEffect(() => {
        const isRoot = location.pathname === '/central' || location.pathname === '/central/';
        if (!isRoot) return;
        const first = findFirstAvailableMenuItem(menuItems);
        if (first?.onClick) first.onClick();
    }, [location.pathname, menuItems]);

    return (
        <MainLayout
            activeFirstMenu="central"
            secondMenu={menuItems}
        >
            <div className="p-6 h-full flex flex-col">
                <Outlet />
            </div>
        </MainLayout>
    );
};

export default CentralLayout;